import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { PageHead } from "@/components/PageHead";

import { AuthTransition } from "../components/AuthTransition";

/**
 * Where an emailed login link lands. Verifies the token and sends the user on.
 *
 * NO CARD AND NO COUNTDOWN. This used to announce "Login Successful!" and count
 * down from three before navigating; a user reported closing the window during
 * it. All that is left is a spinner while the token is exchanged. See
 * AuthTransition for the reasoning and the two sibling pages that match it.
 *
 * WHY THE TOKEN IS EXCHANGED HERE RATHER THAN SERVER-SIDE
 * -------------------------------------------------------
 * A backend redirect would remove this page entirely, but it has no way to hand
 * the access token to the SPA. The two options are the URL — where it lands in
 * browser history, referrer headers and access logs — or a session cookie,
 * which means moving the whole API off bearer auth. Neither is worth it to save
 * a page that no longer delays anybody.
 */
const LoginVerifyTokenPage = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const token = params.get("token");

  const [status, setStatus] = useState<"pending" | "success" | "error">("pending");
  // Only ever shown on failure — see AuthTransition.
  const [message, setMessage] = useState("");
  const [route, setRoute] = useState<string | null>(null);

  const navigate = useNavigate();
  const { verifyLoginToken } = useAuth();
  const hasVerifiedRef = useRef(false);

  useEffect(() => {
    // Runs once: React 18 double-invokes effects in development, and a magic
    // link is single-use, so a second exchange would fail on a spent token.
    if (hasVerifiedRef.current) return;
    hasVerifiedRef.current = true;

    if (!token) {
      setStatus("error");
      setMessage("That login link is missing its token.");
      return;
    }

    verifyLoginToken(token)
      .then(({ route: destination }) => {
        // `route` already accounts for a pending program invitation —
        // verifyLoginToken resolves it server-first (open invitation looked up
        // by email) and falls back to localStorage. Nothing to decide here.
        //
        // Note it deliberately does NOT join the program: an invitation is not
        // an enrolment, so the achiever lands on the program page and presses
        // Enroll themselves. That is also the only path that works for a paid
        // program, where join() 402s until payment clears.
        setStatus("success");
        setRoute(destination);
      })
      .catch(() => {
        setStatus("error");
        setMessage("This login link has expired or has already been used.");
      });
  }, [token, verifyLoginToken]);

  // Navigate as soon as the exchange resolves. Nothing is shown in between —
  // the spinner simply stops being on screen.
  useEffect(() => {
    if (status !== "success" || !route) return;
    // `replace` so Back does not return here. The token is spent by then, and
    // returning would show the failure screen to someone who just signed in.
    navigate(route, { replace: true });
  }, [status, route, navigate]);

  return (
    <>
      <PageHead title="Signing you in" description="Verifying your login link" />
      <AuthTransition
        status={status}
        title="We couldn't sign you in"
        message={message}
        busyLabel="Signing you in"
        action={
          <button
            type="button"
            onClick={() => navigate("/login", { replace: true })}
            className="h-10 cursor-pointer rounded-lg bg-primary-500 px-5 text-sm font-semibold text-white hover:bg-primary-600"
          >
            Request a new link
          </button>
        }
      />
    </>
  );
};

export default LoginVerifyTokenPage;
